import { useState } from "react";
import { useApi } from "../lib/useApi";
import { Button, Card, EmptyState, SectionLabel } from "./ui";
import type { SucursalMin } from "../lib/tipos";

// El dueño atiende en el Mostrador de UNA botica a la vez (Kevin, 8-oct-2026). Antes de vender elige
// en cuál; el equipo la recuerda. Solo boticas activas: en una apagada el servidor no deja vender.

type SucursalFila = SucursalMin & { activa: number };

export function ElegirBotica({ onElegir }: { onElegir: (b: SucursalMin) => void }) {
  const lista = useApi<{ sucursales: SucursalFila[] }>("/sucursales");
  const activas = (lista.data?.sucursales ?? []).filter((s) => s.activa === 1);

  return (
    <div className="mx-auto flex w-full max-w-[520px] flex-col gap-3 p-4" data-testid="elegir-botica">
      <Card className="gap-3">
        <SectionLabel>¿En qué botica estás atendiendo?</SectionLabel>
        <p className="text-[13px] leading-relaxed text-ink-2">
          Las ventas, la mercadería que recibes y el stock que ves serán de esa botica. Este equipo la recuerda; la cambias arriba cuando
          vayas a otra.
        </p>
        {lista.cargando ? (
          <p className="py-2 text-[13px] text-ink-3">Cargando boticas…</p>
        ) : lista.error ? (
          <div>
            <p className="text-[13px] text-accent-ink">{lista.error}</p>
            <button onClick={lista.recargar} className="mt-2 text-[12.5px] text-link underline">
              Reintentar
            </button>
          </div>
        ) : activas.length === 0 ? (
          <EmptyState title="No hay boticas activas" subtitle="Crea o activa una en Ajustes › Boticas." />
        ) : (
          <ul className="flex flex-col gap-2">
            {activas.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => onElegir({ id: s.id, nombre: s.nombre, direccion: s.direccion ?? null })}
                  className="flex min-h-[52px] w-full flex-col items-start justify-center rounded-[10px] border border-line-input bg-field px-3.5 py-2 text-left transition-colors hover:bg-hover-btn"
                >
                  <span className="text-[14px] font-semibold text-ink">{s.nombre}</span>
                  {s.direccion && <span className="text-[12px] text-ink-2">{s.direccion}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

// Arriba del Mostrador y la Recepción: dónde está atendiendo y el botón para cambiar. Cambiar arma un
// Mostrador nuevo, así que una venta a medias se descarta: se pide confirmar.
// `enFila`: en el Mostrador de escritorio va al final de la fila de Faltante · Cliente · … · Reimprimir y no
// en una franja propia (Kevin, 15-oct-2026, en VES: esa franja era lo que hacía bajar la pantalla con scroll).
export function BarraBotica({ botica, onCambiar, enFila = false }: { botica: SucursalMin; onCambiar: () => void; enFila?: boolean }) {
  const [confirmar, setConfirmar] = useState(false);
  return (
    <div
      className={
        enFila
          ? "flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px]"
          : "mb-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-[10px] border border-line-row bg-card px-3 py-2 text-[13px]"
      }
      data-testid="barra-botica"
    >
      <span className="text-ink-2">
        Atiendes en <strong className="font-semibold text-ink">{botica.nombre}</strong>
      </span>
      {!enFila && <span className="flex-1" />}
      {confirmar ? (
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <span className="text-[12px] text-ink-2">Lo que tengas a medias en esta pantalla se descarta.</span>
          {/* Los dos botones van juntos: a 390 px «No» se iba solo a otra línea. */}
          <span className="flex gap-2">
            <Button size="sm" onClick={onCambiar}>
              Sí, cambiar
            </Button>
            <Button size="sm" variant="outline" onClick={() => setConfirmar(false)}>
              No
            </Button>
          </span>
        </span>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setConfirmar(true)}>
          Cambiar de botica
        </Button>
      )}
    </div>
  );
}
